import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doesAgentTypeShowThinking} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useActivityModalControls} from 'llm/chat-library/tool-details/ActivityModal';
import {parseProgressToolCall} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {getToolInputBlock, getToolOutputBlock} from 'llm/chat-library/tool-details/ToolDetail';
import {ToolType, getToolIcon, isToolType} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Label from 'ui-library/bs/Label';
import {
  BLUE_DARKER,
  GRAY_DARK,
  GRAY_DARKER,
  NEAR_BLACK,
  PURPLE_LIGHTEST,
  RED_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import {ProgressData, ProgressToolCall} from '../progress/ProgressTypes';
import {ChatFailureIndicator} from './ChatFailureIndicator';
import {ClientProgressMessage, ProgressStatus} from './MessageTypes';
import {ProgressFailed as SharedProgressFailed} from './ProgressFailed';
import {ThinkingSpinner} from './ThinkingSpinner';
import {ToolActivityItem} from './ToolActivityItem';

const TEST_COMPONENT = 'ChatProgress';

// ============================================================================
// Shared Progress Components
// Used by both Deep Research and Data Capture agents
// ============================================================================

export interface ProgressMessageProps {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

/**
 * A unified progress component for agents that shows an expandable "Thinking" section
 * with tool calls and their outputs. Used by both Deep Research and Data Capture.
 */
export function ProgressMessage({message, progressData, onToolClick}: ProgressMessageProps): JSX.Element {
  const {status, errorMessage} = message;

  switch (status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <ThinkingSection
          progressData={progressData}
          isComplete={false}
          onToolClick={onToolClick}
          errorMessage={errorMessage ?? undefined}
        />
      );
    case ProgressStatus.COMPLETED:
      // Completed state is now handled by ChatProgressMessageBubble using ThinkingSection.
      // This case is kept as a fallback.
      if (progressData && progressData.toolCalls.length > 0) {
        return <ThinkingSection progressData={progressData} isComplete={true} onToolClick={onToolClick} />;
      }
      return <></>;
    case ProgressStatus.FAILED:
      return (
        <SharedProgressFailed
          title="Request failed"
          errorMessage={errorMessage || 'Could not complete request'}
          testComponent={TEST_COMPONENT}
        />
      );
    default:
      throw new Error(`Unknown progress status: ${assertNever(status)}`);
  }
}

// Margin to align vertical line with center of icon
// 12px caret icon: adjusted for visual balance → marginLeft = 3px
// 20px spinner icon: adjusted for visual balance → marginLeft = 9px
const CARET_ICON_MARGIN = 3;
const SPINNER_ICON_MARGIN = 9;

/**
 * Get the display text for a tool call's input, if it should be shown.
 * Returns null if the tool type should not display input.
 * Only Data Capture tools have input display - Deep Research tools return null.
 *
 * IMPORTANT: When adding new tools, also update the backend toolkit in:
 * - benchling/llm/data_capture_assistant/agent/data_capture_agent.py (get_data_capture_agent_toolkit)
 */
function getToolInputDisplay(toolCall: ProgressToolCall): string | null {
  const {tool, args} = toolCall;
  if (!args) {
    return null;
  }
  if (!isToolType(tool)) {
    return null;
  }

  switch (tool) {
    case ToolType.FillTables:
      // Show agent_instructions for FillTables
      if (typeof args.agent_instructions === 'string') {
        return args.agent_instructions;
      }
      return null;

    case ToolType.SearchSchemas:
      // Show search_queries for SearchSchemas
      if (Array.isArray(args.search_queries)) {
        return args.search_queries.join(', ');
      }
      return null;

    case ToolType.AskQuestion:
      // Show question for AskQuestion
      if (typeof args.question === 'string') {
        return args.question;
      }
      return null;

    case ToolType.ReadFiles:
      // Show analysis_prompt for ReadFiles
      if (typeof args.analysis_prompt === 'string') {
        return args.analysis_prompt;
      }
      return null;

    case ToolType.SearchFiles:
      // Show query for SearchFiles
      if (typeof args.query === 'string') {
        return args.query;
      }
      return null;

    case ToolType.SearchBenchlingObjects:
      // Show query for SearchBenchlingObjects (list of strings)
      if (Array.isArray(args.query)) {
        return args.query.join(', ');
      }
      return null;

    case ToolType.SearchEntriesAndTemplates:
      // Show search_queries for SearchEntriesAndTemplates
      if (Array.isArray(args.search_queries)) {
        return args.search_queries.join(', ');
      }
      return null;

    case ToolType.ReviewTool: {
      // Show criteria as a bulleted list and guidelines as content
      const parts: Array<string> = [];
      if (args.criteria && typeof args.criteria === 'object') {
        const criteriaValues = Object.values(args.criteria as Record<string, string>);
        if (criteriaValues.length > 0) {
          parts.push(criteriaValues.map((v) => `- ${v}`).join('\n'));
        }
      }
      if (typeof args.guidelines === 'string' && args.guidelines.trim()) {
        parts.push(args.guidelines);
      }
      return parts.length > 0 ? parts.join('\n\n') : null;
    }

    case ToolType.EditTableCells:
      // Show table_name for EditTableCells
      if (typeof args.table_name === 'string') {
        return args.table_name;
      }
      return null;

    case ToolType.LoadSkill:
      if (typeof args.file_path === 'string' && args.file_path) {
        return args.file_path;
      }
      return null;

    // TODO: Add brief input summaries for these tools.
    case ToolType.Think:
    case ToolType.IdsToModelsTool:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.SearchTool:
    case ToolType.GetWarehouseOverviewTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
    case ToolType.AttachmentTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.InstantiateDocument:
    case ToolType.TranscribeFile:
    case ToolType.UpdateDocument:
    case ToolType.InsertDocumentContent:
    case ToolType.CreateLookupConfig:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
    case ToolType.Respond:
      return null;
    default:
      assertNever(tool);
      return null;
  }
}

interface ReviewIssueData {
  description?: string;
  severity?: string;
  location?: string;
  corrected_value?: string;
}

interface ReviewOutputData {
  status?: string;
  correctness?: number;
  completeness?: number;
  iteration?: number;
  issues?: Array<ReviewIssueData>;
}

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
};

/**
 * Parse the review tool's JSON output into a readable markdown summary.
 * Shows status, scores, iteration count, and issues with descriptions.
 */
function formatReviewOutput(responseText: string): string | null {
  let data: ReviewOutputData;
  try {
    data = JSON.parse(responseText) as ReviewOutputData;
  } catch (error) {
    // JSON.parse throws SyntaxError for invalid JSON
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  const lines: Array<string> = [];

  // Status
  if (data.status) {
    const statusLabels: Record<string, string> = {
      PASSED: 'Passed',
      NEEDS_IMPROVEMENT: 'Needs improvement',
      FLAGGED: 'Flagged for review',
    };
    lines.push(`**${statusLabels[data.status] ?? data.status}**`);
  }

  // Scores
  if (data.correctness != null && data.completeness != null) {
    lines.push(`Correctness: ${data.correctness}% · Completeness: ${data.completeness}%`);
  }

  // Iteration
  if (data.iteration != null) {
    lines.push(`Iteration: ${data.iteration}`);
  }

  // Issues with descriptions
  const issues = data.issues ?? [];
  if (issues.length > 0) {
    const issueLines = issues.map((issue) => {
      const sev = (issue.severity ?? 'minor').toLowerCase();
      const label = SEVERITY_LABELS[sev] ?? sev;
      const location = issue.location ? ` (${issue.location})` : '';
      const corrected = issue.corrected_value ? ` → \`${issue.corrected_value}\`` : '';
      return `- **${label}**: ${issue.description ?? 'No description'}${location}${corrected}`;
    });
    lines.push(`**Issues (${issues.length}):**\n${issueLines.join('\n')}`);
  } else {
    lines.push('No issues found');
  }

  return lines.length > 0 ? lines.join('\n\n') : null;
}

/**
 * Get the display text for a tool call's output, if it should be shown.
 * Returns null if the tool type should not display output.
 * Only Data Capture tools have output display - Deep Research tools return null.
 */
function getToolOutputDisplay(toolCall: ProgressToolCall): string | null {
  const {tool, responseText} = toolCall;
  if (!responseText) {
    return null;
  }
  if (!isToolType(tool)) {
    return null;
  }

  switch (tool) {
    case ToolType.FillTables:
      // Show full output for FillTables
      return responseText;

    case ToolType.SearchSchemas: {
      // Parse output to show count of schemas found
      const schemaMatch = responseText.match(/Found (\d+) (?:entity )?schema/i);
      if (schemaMatch) {
        return `Found ${schemaMatch[1]} schema${schemaMatch[1] === '1' ? '' : 's'}`;
      }
      // Fallback: try to count schema names in the response
      const schemaCount = (responseText.match(/Schema Name:/g) || []).length;
      if (schemaCount > 0) {
        return `Found ${schemaCount} schema${schemaCount === 1 ? '' : 's'}`;
      }
      return null;
    }

    case ToolType.Think:
      // Show full output for Think
      return responseText;

    case ToolType.AskQuestion:
      // AskQuestion responses are not displayed in the output section because
      // they contain internal search results that are not meaningful to show
      // in the progress UI. The agent uses these results internally.
      return null;

    case ToolType.ReadFiles:
      // Show full output for ReadFiles
      return responseText;

    case ToolType.ReviewTool:
      return formatReviewOutput(responseText);

    // TODO: Add brief output summaries for these tools.
    case ToolType.IdsToModelsTool:
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.SearchTool:
    case ToolType.GetWarehouseOverviewTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
    case ToolType.AttachmentTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.InstantiateDocument:
    case ToolType.SearchEntriesAndTemplates:
    case ToolType.SearchBenchlingObjects:
    case ToolType.TranscribeFile:
    case ToolType.EditTableCells:
    case ToolType.UpdateDocument:
    case ToolType.InsertDocumentContent:
    case ToolType.SearchFiles:
    case ToolType.CreateLookupConfig:
    case ToolType.LoadSkill:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
    case ToolType.Respond:
      return null;
    default:
      assertNever(tool);
      return null;
  }
}

/**
 * Renders a tool call in the thinking section.
 *
 * Clickability is determined by whether the tool has structured detail in the
 * activity modal (getToolInputBlock/getToolOutputBlock return non-null).
 * If it does, clicking opens the modal. Inline detail text (getToolInputDisplay/
 * getToolOutputDisplay) is always shown when available, independent of clickability.
 */
function ToolCallItem({
  toolCall,
  onToolClick,
  testKey,
}: {
  toolCall: ProgressToolCall;
  onToolClick?: (toolCall: ProgressToolCall) => void;
  testKey?: string;
}): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const inputDisplay = getToolInputDisplay(toolCall);
  const outputDisplay = getToolOutputDisplay(toolCall);

  const hasStructuredDetail = useMemo(() => {
    const parsed = parseProgressToolCall(toolCall);
    return getToolInputBlock(toolCall, parsed) != null || getToolOutputBlock(toolCall, parsed) != null;
  }, [toolCall]);
  const isClickable = hasStructuredDetail && onToolClick != null;

  return (
    <div
      className={css(progressStyles.toolItem)}
      {...getTestProps({component: TEST_COMPONENT, element: 'toolItem'})}
    >
      <ToolActivityItem
        icon={getToolIcon(toolCall.tool)}
        displayName={getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status)}
        onClick={
          isClickable
            ? () => {
                onToolClick(toolCall);
              }
            : undefined
        }
        testComponent={TEST_COMPONENT}
        testKey={testKey}
      />
      {inputDisplay && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({component: TEST_COMPONENT, element: 'toolInput'})}
        >
          <ToolDetailContent text={inputDisplay} />
        </div>
      )}
      {outputDisplay && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({component: TEST_COMPONENT, element: 'toolOutput'})}
        >
          <ToolDetailContent text={outputDisplay} />
        </div>
      )}
    </div>
  );
}

interface ToolDetailContentProps {
  text: string;
}

const MAX_DETAIL_LENGTH = 500;

function ToolDetailContent({text}: ToolDetailContentProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > MAX_DETAIL_LENGTH;
  const displayText = shouldTruncate && !isExpanded ? `${text.slice(0, MAX_DETAIL_LENGTH)}...` : text;

  return (
    <div className={css(progressStyles.detailContentWrapper)}>
      <div className={css(progressStyles.markdownContent)}>
        <LLMMarkdownSection markdown={displayText} />
      </div>
      {shouldTruncate && (
        <button
          type="button"
          className={css(progressStyles.expandButton)}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export interface ThinkingSectionProps {
  progressData: ProgressData | null;
  isComplete: boolean;
  onToolClick?: (toolCall: ProgressToolCall) => void;
  errorMessage?: string;
}

/**
 * Unified thinking section component for both in-progress and completed states.
 * - In progress: shows spinner, always expanded, displays "Working on your request..." when no tools
 * - Completed: shows caret button, collapsible (default collapsed)
 */
export function ThinkingSection({
  progressData,
  isComplete,
  onToolClick,
  errorMessage,
}: ThinkingSectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(!isComplete);
  const hasToolCalls = progressData && progressData.toolCalls.length > 0;
  const toolCount = progressData?.toolCalls.length ?? 0;
  const stepCountText = toolCount > 0 ? ` • ${toolCount} ${toolCount === 1 ? 'step' : 'steps'}` : '';

  // Determine effective expansion state: always expanded when in progress
  const effectivelyExpanded = isComplete ? isExpanded : true;
  const iconMargin = isComplete ? CARET_ICON_MARGIN : SPINNER_ICON_MARGIN;

  return (
    <div
      className={css(progressStyles.completedContainer)}
      {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
    >
      {/* Header: spinner when in progress, collapsible button when complete */}
      {isComplete ? (
        <button
          type="button"
          className={css(progressStyles.completedHeader)}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          {...getTestProps({component: TEST_COMPONENT, element: 'expandButton'})}
        >
          <BenchlingIcon
            icon={isExpanded ? 'caret-down' : 'caret-right'}
            className={css(progressStyles.expandIcon)}
          />
          <Label className={css(progressStyles.completedText)}>Thinking{stepCountText}</Label>
        </button>
      ) : (
        <div className={css(progressStyles.inProgressHeader)}>
          <ThinkingSpinner text="Thinking" testComponent={TEST_COMPONENT} />
          {toolCount > 0 && <Label className={css(progressStyles.stepCountText)}>{stepCountText}</Label>}
        </div>
      )}

      {/* Content: tool list or "Working on your request..." message */}
      {effectivelyExpanded && (
        <div
          className={css(progressStyles.expandedContentBase)}
          style={{marginLeft: iconMargin}}
          {...getTestProps({component: TEST_COMPONENT, element: 'content'})}
        >
          {!isComplete && !hasToolCalls ? (
            <Label className={css(progressStyles.analyzingText)}>Working on your request...</Label>
          ) : progressData ? (
            progressData.toolCalls.map((toolCall, index) => (
              <ToolCallItem
                key={toolCall.toolId || index}
                toolCall={toolCall}
                onToolClick={onToolClick}
                testKey={`${index}`}
              />
            ))
          ) : null}
        </div>
      )}

      {/* Error message (only shown when in progress) */}
      {errorMessage && (
        <div className={css(progressStyles.errorContent)}>
          <span className={css(agentProgressStyles.errorMessage)}>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Chat Progress Message Bubble
// Entry point for rendering progress messages in the chat
// ============================================================================

/**
 * Render a ClientProgressMessage taking status into account.
 *
 * If the message indicates a progress status, show a loading message.
 * If the message indicates a completed status, show nothing.
 * If the message indicates a failed status, show a failure indicator.
 */
export function ActiveChatProgressMessageBubble({
  message,
  inProgressText = 'Processing your request...',
}: {
  message: ClientProgressMessage;
  inProgressText?: string | Array<string>;
}): JSX.Element | null {
  switch (message.status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div className={css(agentProgressStyles.progressContainer)}>
          <LLMLoadingMessage text={inProgressText} fontSize={14} />
        </div>
      );
    case ProgressStatus.COMPLETED:
      return null;
    case ProgressStatus.FAILED:
      return <ChatFailureIndicator errorMessage={message.errorMessage} />;
    default:
      assertNever(message.status);
      return null;
  }
}

/**
 * Render the appropriate progress message bubble based on the agent type.
 */
export function ChatProgressMessageBubble({
  message,
  progressData,
}: {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
}): JSX.Element {
  const {openActivityModal} = useActivityModalControls();

  // For active states (STARTED/IN_PROGRESS), only use message's progressData to show
  // current exchange tool calls. Don't fall back to progressData prop which contains ALL tool calls.
  // For COMPLETED status, we can fall back to progressData prop since it's used for completed sections
  // that may need access to all tool calls.
  const effectiveProgressData =
    message.status === ProgressStatus.COMPLETED
      ? (message.progressData ?? progressData ?? null)
      : (message.progressData ?? null);

  const handleToolClick = (toolCall: ProgressToolCall): void => {
    if (effectiveProgressData) {
      openActivityModal(effectiveProgressData, toolCall.toolId);
    }
  };

  // Handle COMPLETED status for agents with thinking sections
  if (
    message.status === ProgressStatus.COMPLETED &&
    message.agentType != null &&
    doesAgentTypeShowThinking(message.agentType) &&
    effectiveProgressData &&
    effectiveProgressData.toolCalls.length > 0
  ) {
    return (
      <ThinkingSection progressData={effectiveProgressData} isComplete={true} onToolClick={handleToolClick} />
    );
  }

  switch (message.agentType) {
    case AgentType.DEEP_RESEARCH:
    case AgentType.DATA_CAPTURE:
    case AgentType.ONBOARDING:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.CO_SCIENTIST:
    case AgentType.ASSISTANT:
      return (
        <ProgressMessage
          message={message}
          progressData={effectiveProgressData}
          onToolClick={handleToolClick}
        />
      );
    case AgentType.ASK:
      return (
        <ActiveChatProgressMessageBubble
          message={message}
          inProgressText={[
            'Processing your request...',
            'Analyzing your question...',
            'Gathering information...',
            'Generating answer...',
          ]}
        />
      );
    case AgentType.ANALYSIS:
      return (
        <ActiveChatProgressMessageBubble
          message={message}
          inProgressText={[
            'Processing your request...',
            'Running analysis...',
            'Gathering results...',
            'Generating response...',
          ]}
        />
      );
    case null:
      return <ActiveChatProgressMessageBubble message={message} />;
    default:
      assertNever(message.agentType);
      return <ActiveChatProgressMessageBubble message={message} />;
  }
}

// ============================================================================
// Styles
// ============================================================================

const TEXT_COLOR = NEAR_BLACK;
const BORDER_COLOR = GRAY_DARK;

/**
 * Styles specific to the unified progress components.
 */
const progressStyles = StyleSheet.create({
  completedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '12px 0',
    gap: '0',
    backgroundColor: WHITE,
  },

  completedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },

  completedText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  inProgressHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  stepCountText: {
    ...FONT_PROXIMA_14,
    color: TEXT_COLOR,
    margin: 0,
  },

  expandIcon: {
    color: TEXT_COLOR,
    fontSize: '12px',
  },

  analyzingText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  errorContent: {
    marginTop: '8px',
  },

  // Base expanded content style (tool list) with vertical line
  expandedContentBase: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    paddingLeft: '16px',
    borderLeft: `2px solid ${BORDER_COLOR}`,
    background: 'transparent',
  },

  // Tool item styles
  toolItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    background: 'transparent',
  },

  toolName: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    background: 'transparent',
    margin: 0,
  },

  toolDetailText: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    marginTop: '4px',
    marginLeft: '16px',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  detailContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  markdownContent: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    background: 'transparent',
    lineHeight: '1.5',
    wordBreak: 'break-word',
    // Override markdown section default styles
    '& section': {
      background: 'transparent',
    },
    '& p': {
      margin: '0.5em 0',
    },
    '& ul, & ol': {
      margin: '0.5em 0',
      paddingLeft: '1.5em',
    },
    '& li': {
      margin: '0.25em 0',
    },
    '& code': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '0.9em',
    },
    '& pre': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '8px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '0.5em 0',
    },
    '& pre code': {
      background: 'transparent',
      padding: 0,
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '0.5em 0',
    },
    '& th, & td': {
      border: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '4px 8px',
    },
    '& th': {
      background: 'rgba(0, 0, 0, 0.05)',
    },
  },

  expandButton: {
    ...FONT_PROXIMA_12,
    color: BLUE_DARKER,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    marginTop: '4px',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

/**
 * Shared styles for agent progress components.
 * Used by deep research and data capture progress UIs.
 */
export const agentProgressStyles = StyleSheet.create({
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  failedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '4px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: RED_LIGHTEST,
  },

  progressHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '8px',
  },

  thinkingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
  },

  thinkingText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  errorMessage: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: GRAY_DARK,
  },

  activityItemClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    borderRadius: '4px',
  },

  activityIcon: {
    width: '16px',
    textAlign: 'center',
  },

  activityDescription: {
    ...FONT_PROXIMA_14,
    fontSize: '13px',
    color: GRAY_DARKER,
    flex: 1,
  },

  toolDescription: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
